import { DateTime } from 'luxon';

import { translateText } from '../components/TranslatedLabel/TranslatedLabel';
import {
  DEFAULT_DATE_FORMAT,
  LanguageLocale,
  PAYMENT_EXPIRATION_DATE_FORMAT,
  TIME_FORMAT_WITH_TIMEZONE,
} from '../constants/common.constant';
import { DAY, daysBetween, format, isSameDate } from './date-time.utils';

export const formatDateTimeForAPI = (
  dateTime: DateTime,
  format: string
): string => dateTime.toUTC().toFormat(format).replace(' ', 'T');

export const formatFromDateTimeObj = (
  dateTime: DateTime,
  format: string = DEFAULT_DATE_FORMAT,
  locale: string = LanguageLocale.EN,
  timeZone?: string
): string => {
  return dateTime.setLocale(locale).setZone(timeZone).toFormat(format);
};

export function formatToDefaultDateFormat(dateString: string) {
  return DateTime.fromISO(dateString, { zone: 'utc' }).toFormat(
    DEFAULT_DATE_FORMAT
  );
}

/**
 * Formats a UTC date string according to specific display rules.
 * Display Rules
 * - In the last seven days - [Day of the week] at xx:xx AM / PM
 * - Seven days to one year - [Month] [Day] at xx:xx AM / PM
 * @param {string} utcDateString - The UTC date string to format.
 * @returns {string} The formatted date string.
 */
export function formatNotificationDate(utcDateString: string): string {
  const date = new Date(utcDateString); // Parse the UTC date string into a Date object
  const now = new Date(); // Get the current date

  // Calculate the difference in days
  const differenceInTime = now.getTime() - date.getTime();
  const differenceInDays = Math.floor(differenceInTime / (1000 * 3600 * 24));

  // Helper function to format the time
  const formatTime = (date: Date) => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const isAM = hours < 12;

    const formattedHours = hours % 12 || 12; // Convert 24-hour format to 12-hour format
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes; // Add leading zero to minutes

    return `${formattedHours}:${formattedMinutes} ${isAM ? 'AM' : 'PM'}`;
  };

  // Helper function to get the day of the week
  const getDayOfWeek = (date: Date) => {
    const daysOfWeek = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    return daysOfWeek[date.getDay()];
  };

  // Helper to check if the date is today
  const isToday = now.toDateString() === date.toDateString();

  // Helper function to get the month and day
  const getMonthAndDay = (date: Date) => {
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    const month = months[date.getMonth()];
    const day = date.getDate();

    return `${month} ${day}`;
  };

  // Formatting logic based on the difference in days
  if (isToday) {
    // If the date is today
    return `Today at ${formatTime(date)}`;
  } else if (differenceInDays < 7) {
    // In the last seven days
    return `${getDayOfWeek(date)} at ${formatTime(date)}`;
  } else if (differenceInDays < 365) {
    // Between seven days and one year
    return `${getMonthAndDay(date)} at ${formatTime(date)}`;
  } else {
    // Beyond one year
    return `${getMonthAndDay(date)}, ${date.getFullYear()} at ${formatTime(
      date
    )}`;
  }
}

/**
 * Formats a UTC date string according to specific display rules.
 * Display Rules
 * - In the last seven days - [Day of the week] at xx:xx AM / PM
 * - Seven days to one year - [Month] [Day] at xx:xx AM / PM
 * @param {string} utcDateString - The UTC date string to format.
 * @param {string} locale - Result locale.
 * @param {string} timeZone - Result timeZone.
 * @returns {string} The formatted date string in the specified timezone.
 */
export function formatCompletionDateToTimezone(
  utcDateString: string,
  locale: string = LanguageLocale.EN,
  timeZone?: string
): string {
  const dateWithZone = DateTime.fromISO(utcDateString)
    .setLocale(locale)
    .setZone(timeZone);
  const dateUTC = dateWithZone.toJSDate();

  // Calculate the difference in days
  const differenceInDays = Math.abs(daysBetween(new Date(), dateUTC));
  const isToday = isSameDate(
    utcDateString,
    new Date(Date.now()).toISOString(),
    timeZone
  );
  const isTomorrow = isSameDate(
    utcDateString,
    new Date(Date.now() + DAY).toISOString(),
    timeZone
  );

  // Helper function to format the time
  const formatTime = (date: Date) => {
    return format(date, TIME_FORMAT_WITH_TIMEZONE, locale, timeZone);
  };

  // Helper function to get the day of the week
  const getDayOfWeek = (date: DateTime) => {
    return date.weekdayLong;
  };

  // Helper function to get the month and day
  const getMonthAndDay = (date: DateTime) => {
    const monthName = date.monthLong;
    const day = date.toJSDate().getDate();
    return `${monthName} ${day}`;
  };

  // Formatting logic based on the difference in days
  if (isToday) {
    // If the date is today
    return `${formatTime(dateUTC)}`;
  }
  if (isTomorrow) {
    // If the date is tomorrow
    return `${formatTime(dateUTC)} ${translateText('common:dateRange.tomorrow')}`;
  }
  if (differenceInDays < 7) {
    // In the last/following seven days
    return `${formatTime(dateUTC)} ${getDayOfWeek(dateWithZone)}`;
  }
  if (differenceInDays < 365) {
    // Between seven days and one year
    return `${getMonthAndDay(dateWithZone)} at ${formatTime(dateUTC)}`;
  }
  // Beyond one year
  return `${getMonthAndDay(dateWithZone)}, ${dateWithZone.year} at ${formatTime(dateUTC)}`;
}

export const formatExpirationDate = (month?: number, year?: number) => {
  if (!month || !year) {
    return '';
  }
  const date = DateTime.fromObject({ month, year });
  return date.toFormat(PAYMENT_EXPIRATION_DATE_FORMAT);
};

/**
 * Parses an expiration date string in MM/YY format and returns month and year as numbers
 * @param expirationDate - The expiration date string in MM/YY format
 * @returns Object with expirationMonth and expirationYear as numbers
 * @note Currently strictly handles MM/YY format only. Can be enhanced to support additional formats (M/YY, MM-YY, etc.) as needed.
 */
export const parseExpirationDate = (expirationDate: string) => {
  const { month, year, isValid, invalidReason } = DateTime.fromFormat(
    expirationDate,
    PAYMENT_EXPIRATION_DATE_FORMAT
  );

  if (!isValid) {
    throw new Error(`Invalid expiration date: ${invalidReason}`);
  }

  return {
    expirationMonth: month,
    expirationYear: year,
  };
};
